import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import {
  Form,
  Input,
  Button,
  Select,
  Upload,
  message,
  Typography,
  Row,
  Col,
  Image
} from "antd";
import {
  ArrowLeftOutlined,
  SaveOutlined,
  UploadOutlined,
  PlusOutlined
} from "@ant-design/icons";
import LocationSelector from "components/inputs/LocationSelector";
import warehouseApi from "../api/warehouseApi";
import "./CreateWarehousePage.scss";

const { Text } = Typography;
const { Option } = Select;

const CreateWarehousePage = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const history = useHistory();

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      // Validate location is selected
      if (!selectedLocation) {
        message.error("Please select a location for the warehouse");
        setLoading(false);
        return;
      }

      // Create warehouse data
      const warehouseData = {
        name: values.warehouseName,
        category: values.category || "Distribution",
        address: selectedLocation.address,
        image: values.image || null,
        savedLocationId: selectedLocation.id
      };

      // Call API to create warehouse
      await warehouseApi.createWarehouse(warehouseData);

      message.success("Warehouse created successfully");
      history.push("/warehouses");
    } catch (error) {
      console.error("Error creating warehouse:", error);
      message.error("Failed to create warehouse");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    history.push("/warehouses");
  };

  const validateImageFileType = (file) => {
    const allowedTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/webp",
      "image/heic"
    ];
    return allowedTypes.includes(file.type.toLowerCase());
  };

  const validateImageFileSize = (file) => {
    const maxSize = 5 * 1024 * 1024;
    return file.size <= maxSize;
  };

  const handleFileUpload = (file) => {
    if (!validateImageFileType(file)) {
      message.error("Only JPG, JPEG, PNG, WebP, and HEIC image files are allowed");
      return Upload.LIST_IGNORE;
    }

    if (!validateImageFileSize(file)) {
      message.error("File size must be less than 5MB");
      return Upload.LIST_IGNORE;
    }

    const imageUrl = URL.createObjectURL(file);
    setImageFile(imageUrl);
    form.setFieldValue("image", imageUrl);
    return false;
  };

  return (
    <div className="create-warehouse-page">
      <div className="page-header">
        <Button
          type="link"
          icon={<ArrowLeftOutlined />}
          onClick={handleCancel}
          className="back-button"
        >
          Back to Warehouses
        </Button>
        <h1 className="page-title">Create New Warehouse</h1>
      </div>

      <div className="form-content">
        <Form
          id="warehouse-form"
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          className="warehouse-form"
        >
          <Row gutter={[24, 0]}>
            <Col span={12}>
              <Form.Item
                name="warehouseName"
                label="Warehouse Name"
                rules={[{ required: true, message: "Please enter warehouse name" }]}
              >
                <Input placeholder="Enter warehouse name" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="category"
                label="Warehouse Category"
                rules={[{ required: true, message: "Please select a category" }]}
              >
                <Select placeholder="Select a Category">
                  <Option value="Distribution">Distribution</Option>
                  <Option value="Storage">Storage</Option>
                  <Option value="Manufacturing">Manufacturing</Option>
                  <Option value="Retail">Retail</Option>
                  <Option value="Other">Other</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[24, 0]}>
            <Col span={24}>
              <Form.Item label="Warehouse Location" required>
                <LocationSelector
                  value={selectedLocation}
                  onChange={setSelectedLocation}
                  placeholder="Select a saved location for this warehouse"
                />
              </Form.Item>
              {selectedLocation && (
                <div
                  className="selected-location-info"
                  style={{
                    marginTop: "8px",
                    padding: "12px",
                    backgroundColor: "#f6ffed",
                    border: "1px solid #b7eb8f",
                    borderRadius: "6px",
                    display: "flex",
                    gap: "16px"
                  }}
                >
                  {selectedLocation.image && (
                    <div
                      style={{
                        width: 100,
                        height: 100,
                        border: "2px solid #b7eb8f",
                        borderRadius: 8,
                        overflow: "hidden",
                        flexShrink: 0
                      }}
                    >
                      <Image
                        src={selectedLocation.image}
                        alt={selectedLocation.locationName}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover"
                        }}
                        preview={true}
                      />
                    </div>
                  )}
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: "bold", color: "#52c41a" }}>
                      📍 Selected Location:
                    </div>
                    <div style={{ marginTop: "4px" }}>
                      <strong>{selectedLocation.locationName}</strong>
                    </div>
                    <div style={{ color: "#666", fontSize: "14px" }}>
                      {selectedLocation.address}
                    </div>
                    {selectedLocation.lat && selectedLocation.lng && (
                      <div style={{ color: "#999", fontSize: "12px", marginTop: "4px" }}>
                        Coordinates: {selectedLocation.lat}, {selectedLocation.lng}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </Col>
          </Row>
        </Form>

        <div className="form-footer">
          <Button onClick={handleCancel} className="cancel-button">
            Cancel
          </Button>
          <Button
            type="primary"
            htmlType="submit"
            form="warehouse-form"
            loading={loading}
            className="save-button"
          >
            <SaveOutlined /> {loading ? "Creating..." : "Create Warehouse"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CreateWarehousePage;
